import React, { Suspense, useEffect } from 'react';
import { NavLink, Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { api, useCachedApi, useManifest, resourceVersion, prefetchDashboard } from '../lib/api';
import { initWebPush } from '../lib/messaging';
import { getPendingJoin, clearPendingJoin } from '../lib/pendingJoin';
import { getPendingReferral, clearPendingReferral } from '../lib/pendingReferral';
import { Avatar } from './ui';
import BirthYearGate from './BirthYearGate';
import '../Dashboard.css';

const NAV = [
  { to: '/app', label: 'Home', icon: '◎', end: true },
  { to: '/app/moments', label: 'Moments', icon: '✦' },
  { to: '/app/circles', label: 'Circles', icon: '⬡' },
  { to: '/app/friends', label: 'Friends', icon: '👥' },
  { to: '/app/insights', label: 'Insights', icon: '📈' },
  { to: '/app/badges', label: 'Badges', icon: '🏅' },
  { to: '/app/profile', label: 'Profile', icon: '☉' },
];

export default function DashboardLayout() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  // Profile is shared chrome (sidebar identity) — cached against the manifest's profile
  // stamp, so the shell and the Profile page share one response.
  const { manifest, loading: lm } = useManifest();
  const { data } = useCachedApi('/auth/profile', resourceVersion(manifest, lm, 'profile'));
  const profile = data?.profile || {};

  // Once the shell knows the manifest, warm every page's data in the background so the
  // user's first navigation to Circles/Friends/Moments/Badges is instant.
  useEffect(() => { if (manifest) prefetchDashboard(manifest); }, [manifest]);

  // Register web push once the user is in the authenticated shell (QA P0-1). Best-effort and
  // self-gating: no-ops without a VAPID key, on unsupported browsers, or if the user declines
  // permission — the polling path (P0-2) still keeps web near-real-time in those cases.
  useEffect(() => { initWebPush(); }, []);

  // Finish a pending invite-link join: a friend who created an account from an invite link
  // lands here after verifying + signing in (possibly in a new tab that lost the /join
  // route). Redeem the stashed code now so they end up in the circle without reopening it.
  useEffect(() => {
    const code = getPendingJoin();
    if (!code) return;
    let cancelled = false;
    api.post('/crews/join', { inviteCode: code })
      .then((res) => {
        clearPendingJoin();
        const crewId = res.data?.crew?.id;
        if (!cancelled && crewId) navigate(`/app/circles/${crewId}`);
      })
      .catch((err) => {
        // Stop retrying on a dead code; leave it for a transient network error to retry next load.
        if (err?.response?.data?.error === 'crew_not_found') clearPendingJoin();
      });
  }, [navigate]);

  // Finish a pending personal-invite (profile link) the same way: a new account that arrived via
  // a /r/<code> link redeems it here, crediting the inviter's Invite badge. No navigation needed —
  // they're already in the app. Idempotent on the backend, so an existing account is a harmless no-op.
  useEffect(() => {
    const code = getPendingReferral();
    if (!code) return;
    api.post('/auth/invite/redeem', { code })
      .then(() => clearPendingReferral())
      .catch((err) => {
        // Clear on a definitive answer (invalid code); keep it for a transient network error.
        if (err?.response?.data?.error === 'invite_invalid') clearPendingReferral();
      });
  }, []);

  const onSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="db">
      {/* OAuth/legacy age-gate backfill: a null birthYear (server flag needsBirthYear) blocks the
          whole shell with the birth-year question until answered (Register asks it at signup;
          this catches Google/Apple sign-ups and pre-gate accounts). */}
      {profile.needsBirthYear === true && <BirthYearGate />}
      <div className="db-shell">
        <aside className="db-side">
          <Link to="/app" className="db-side-brand">
            <img className="db-mark" src="/spheresync-icon.png" alt="" aria-hidden="true" />
            Sphere<span className="db-grad">Sync</span>
          </Link>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `db-nav-link${isActive ? ' is-active' : ''}`}
            >
              <span className="db-nav-ico">{item.icon}</span>{item.label}
            </NavLink>
          ))}
          <div className="db-side-foot">
            <div className="db-side-user">
              <Avatar src={profile.avatarImageUrl || profile.avatarUrl} name={profile.displayName} size={36} />
              <div>
                <b>{profile.displayName || 'You'}</b>
                <small>{profile.email || ''}</small>
              </div>
            </div>
            <button className="db-btn db-btn-sm" onClick={onSignOut}>Sign out</button>
          </div>
        </aside>

        <div className="db-content">
          <nav className="db-mobile-bar">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) => (isActive ? 'is-active' : '')}>
                {item.label}
              </NavLink>
            ))}
            <button onClick={onSignOut} className="db-mobile-signout">Sign out</button>
          </nav>
          <main className="db-main">
            {/* Nested chunk boundary (PERF-16 fix): a lazy page's first load suspends HERE, so
                the shell (nav + dark background) stays mounted instead of the app-level
                fallback blanking the whole screen — that was the white flash on first visit
                to each page. Subsequent visits are cache hits and never suspend. */}
            <Suspense fallback={<div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </div>
  );
}
